import dayjs from "dayjs"
import { Field, FormikHelpers } from "formik"
import useModalTrigger from "magik-react-hooks/useModalTrigger"
import { useCallback, useMemo, useState } from "react"
import { Button } from "react-bootstrap"
import { MdDelete, MdEdit, MdFileDownload, MdFilePresent, MdFolder } from "react-icons/md"
import { Link, useParams } from "react-router-dom"
import * as yup from "yup"
import { useAuth } from "../../auth"
import { VField } from "../../components/Fields/Fields"
import { InputField } from "../../components/Fields/InputField"
import ModalConfirm from "../../components/ModalConfirm"
import ModalForm from "../../components/ModalForm"
import StandardLayout from "../../components/StandardLayout"
import { FS_PERMISSIONS } from "../../constants"
import {
  useCreateFolder,
  useDeleteFile,
  useDeleteFolder,
  useDownloadFile,
  useFolder,
  useRenameFile,
  useUpdateFolder,
} from "../../hooks/filessystems"
import { FileType, FolderBaseType } from "../../types"
import UploadFileModal from "./UploadFileModal"

const schema_renameFile = yup.object().shape({
  new_name: yup.string().label("new_name"),
  description: yup.string().label("description"),
})
const schema_renameFolder = yup.object().shape({
  name: yup.string().label("name").required(),
  description: yup.string().label("description"),
})

const schema_createFolder = yup.object().shape({
  name: yup.string().label("name").required(),
})

const FilesSystem = () => {
  const { id: folderId } = useParams()
  const { user } = useAuth()
  const { data, refetch, error } = useFolder(folderId ?? "0")

  const downloadFile = useDownloadFile()
  const { mutateAsync: renameFile } = useRenameFile()
  const { mutateAsync: deleteFile } = useDeleteFile()
  const { mutateAsync: createFolder } = useCreateFolder(folderId ?? "0")
  const { mutateAsync: renameFolder } = useUpdateFolder()
  const { mutateAsync: deleteFolder } = useDeleteFolder()

  const [uploadFileModalState, uploadFileModalActions] = useModalTrigger()
  const [renameFileModalState, renameFileModalActions] = useModalTrigger<FileType>()
  const [deleteFileModalState, deleteFileModalActions] = useModalTrigger<FileType>()
  const [createFolderModalState, createFolderModalActions] = useModalTrigger()
  const [renameFolderModalState, renameFolderModalActions] = useModalTrigger<FolderBaseType>()
  const [deleteFolderModalState, deleteFolderModalActions] = useModalTrigger<FolderBaseType>()

  const [isDeleteModalSubmitting, setDeleteIsModalSubmitting] = useState(false)
  const [deleteError, setDeleteError] = useState<string | null>(null)

  const path = useMemo(() => {
    const result: Record<number, string> = { 0: "Files" }
    if (!data?.folder) return result
    const pathArray = data.folder.path.split("/").filter(Boolean)
    const pathArrayName = data.folder.path_by_name.split("/").filter(Boolean)
    for (const index in pathArrayName) {
      result[Number(pathArray[index])] = pathArrayName[index]
    }
    return result
  }, [data?.folder])

  const confirmDeleteFile = useCallback(
    (fileId: number) => {
      setDeleteIsModalSubmitting(true)
      return deleteFile(fileId)
        .then(() => {
          deleteFileModalActions.close()
          refetch()
        })
        .finally(() => setDeleteIsModalSubmitting(false))
    },
    [deleteFileModalActions, deleteFile, refetch]
  )

  const createNewFolder = useCallback(
    (values: { name: string; description: string }, actions: FormikHelpers<{ name: string; description: string }>) => {
      return createFolder({ name: values.name, description: values.description ?? "" })
        .then(() => {
          refetch()
          createFolderModalActions.close()
          actions.resetForm()
        })
        .catch((e) => {
          actions.setErrors(e.data)
        })
    },
    [createFolderModalActions, createFolder, refetch]
  )

  const confirmRenameFolder = useCallback(
    (values: { name: string; description: string }, actions: FormikHelpers<{ name: string; description: string }>) => {
      if (!renameFolderModalState.value) return Promise.resolve()
      return renameFolder({ ...renameFolderModalState.value, name: values.name, description: values.description ?? "" })
        .then(() => {
          refetch()
          renameFolderModalActions.close()
          actions.resetForm()
        })
        .catch((e) => {
          actions.setErrors(e.data)
        })
    },
    [renameFolderModalActions, renameFolderModalState.value, renameFolder, refetch]
  )

  const confirmDeleteFolder = useCallback(
    (folderId: number) => {
      setDeleteIsModalSubmitting(true)
      return deleteFolder(folderId)
        .then(() => {
          deleteFolderModalActions.close()
          refetch()
        })
        .catch((e) => {
          setDeleteError(e.data?.detail ?? "Errore durante l'eliminazione della cartella")
        })
        .finally(() => setDeleteIsModalSubmitting(false))
    },
    [deleteFolderModalActions, deleteFolder, refetch]
  )

  const confirmRenameFile = useCallback(
    (
      values: { new_name: string; description: string },
      actions: FormikHelpers<{ new_name: string; description: string }>
    ) => {
      if (!renameFileModalState.value) return Promise.resolve()

      return renameFile({
        file: renameFileModalState.value,
        newName: values.new_name,
        newDescription: values.description,
      })
        .then(() => {
          refetch()
          renameFileModalActions.close()
          actions.resetForm()
        })
        .catch((e) => {
          actions.setErrors(e.data)
        })
    },
    [renameFileModalActions, renameFileModalState.value, renameFile, refetch]
  )

  return (
    <>
      <StandardLayout>
        <div className="sticky-header bg-light p-3 shadow-sm">
          <div className="d-flex justify-content-between">
            <div className="page-title">
              {Object.keys(path ?? []).map((key, i, list) => (
                <span key={key}>
                  {i === list.length - 1 ? (
                    <span>{path[Number(key)]}</span>
                  ) : (
                    <Link to={`/files-system/${key}`}>{path[Number(key)]}</Link>
                  )}
                  {i !== list.length - 1 && " / "}
                </span>
              ))}
            </div>
            <div className="d-flex align-items-center gap-2">
              {((data?.folder?.user_permission ?? FS_PERMISSIONS.NO_ACCESS) >= FS_PERMISSIONS.WRITE_FOLDER ||
                user?.is_superuser) && (
                <Button variant="outline" size="sm" onClick={() => createFolderModalActions?.open("dummy")}>
                  Aggiungi Cartella
                </Button>
              )}
              {(data?.folder?.user_permission ?? FS_PERMISSIONS.NO_ACCESS) >= FS_PERMISSIONS.WRITE_FILE && folderId && (
                <Button size="sm" onClick={() => uploadFileModalActions.open("dummy")}>
                  Aggiungi File
                </Button>
              )}
            </div>
          </div>
        </div>
        <div className="px-3 py-4">
          {error && <div className="text-danger error-field">{(error as any).data.detail} </div>}
          {(data?.subfolders ?? [])
            .sort((a, b) => a.name.localeCompare(b.name))
            .map((folder) => (
              <div key={folder.id} className="d-flex align-items-center justify-content-between fs-hover pe-3">
                <Link
                  to={`/files-system/${folder.id}`}
                  className="d-flex align-items-center text-decoration-none text-dark flex-grow-1 py-3 ps-3"
                >
                  <MdFolder size="24" className="me-3" />
                  <div>
                    <div>{folder.name}</div>
                    <div className="text-gray fw-light fs-14">{folder.description}</div>
                  </div>
                </Link>
                <div className="d-flex align-items-center">
                  <MdEdit
                    size="20"
                    className="ms-3 pointer"
                    onClick={() => renameFolderModalActions?.open(folder)}
                    opacity={
                      (data?.folder?.user_permission ?? FS_PERMISSIONS.NO_ACCESS) >= FS_PERMISSIONS.WRITE_FOLDER ? 1 : 0
                    }
                  />
                  <MdDelete
                    size="20"
                    className="ms-3 pointer"
                    onClick={() => deleteFolderModalActions.open(folder)}
                    opacity={
                      (data?.folder?.user_permission ?? FS_PERMISSIONS.NO_ACCESS) >= FS_PERMISSIONS.WRITE_FOLDER ? 1 : 0
                    }
                  />
                  <div className="ms-3">{dayjs(folder.updated_at).format("DD/MM/YYYY HH:mm")}</div>
                </div>
              </div>
            ))}
          {(data?.files ?? [])
            .sort((a, b) => a.name.localeCompare(b.name))
            .map((file) => (
              <div key={file.id} className="d-flex align-items-center text-dark justify-content-between fs-hover p-3">
                <div className="d-flex align-items-center">
                  <MdFilePresent size="24" className="me-3 flex-shrink-0" />
                  <div>
                    <div>
                      {file.name}
                      {file.ext}
                    </div>
                    <div className="text-gray fw-light fs-14">{file.description}</div>
                  </div>
                </div>
                <div className="d-flex align-items-center flex-shrink-0">
                  <MdFileDownload
                    size="20"
                    className="ms-auto pointer"
                    onClick={() => downloadFile(file)}
                    opacity={
                      (data?.folder?.user_permission ?? FS_PERMISSIONS.NO_ACCESS) >= FS_PERMISSIONS.READ_FILE ? 1 : 0
                    }
                  />
                  <MdEdit
                    size="20"
                    className="ms-3 pointer"
                    onClick={() => renameFileModalActions?.open(file)}
                    opacity={
                      (data?.folder?.user_permission ?? FS_PERMISSIONS.NO_ACCESS) >= FS_PERMISSIONS.WRITE_FILE ? 1 : 0
                    }
                  />
                  <MdDelete
                    size="20"
                    className="ms-3 pointer"
                    onClick={() => deleteFileModalActions.open(file)}
                    opacity={
                      (data?.folder?.user_permission ?? FS_PERMISSIONS.NO_ACCESS) >= FS_PERMISSIONS.WRITE_FILE ? 1 : 0
                    }
                  />
                  <div className="ms-3">{dayjs(file.updated_at).format("DD/MM/YYYY HH:mm")}</div>
                </div>
              </div>
            ))}
        </div>
      </StandardLayout>
      {deleteFileModalState.value && (
        <ModalConfirm
          isOpen={deleteFileModalState.isOpen}
          toggle={deleteFileModalActions.toggle}
          onClosed={deleteFileModalActions.onClosed}
          title="Elimina File"
          body={`Confermi l'eliminazione del file ${deleteFileModalState.value!.name}?`}
          okCallBack={() => confirmDeleteFile(deleteFileModalState.value!.id)}
          isSubmitting={isDeleteModalSubmitting}
        />
      )}
      {renameFileModalState.value && (
        <ModalForm<{ new_name: string; description: string }>
          title={`Modifica File - ${renameFileModalState.value.name}${renameFileModalState.value.ext}`}
          isOpen={renameFileModalState.isOpen}
          toggle={renameFileModalActions.toggle}
          initialValues={{
            new_name: `${renameFileModalState.value.name}${renameFileModalState.value.ext}`,
            description: renameFileModalState.value.description ?? "",
          }}
          onClosed={renameFileModalActions.onClosed}
          save={confirmRenameFile}
          schema={schema_renameFile}
        >
          <div className="row">
            <div className="col-12">
              <VField
                name="new_name"
                label={"Nuovo nome"}
                placeholder={`${renameFileModalState.value.name}${renameFileModalState.value.ext}`}
              />
            </div>
          </div>
          <div className="row">
            <div className="col-12">
              <Field
                component={InputField}
                name="description"
                label={"Nuova descrizione"}
                placeholder={`${renameFileModalState.value.description ?? ""}`}
                type="textarea"
                textarea
              />
            </div>
          </div>
        </ModalForm>
      )}
      {renameFolderModalState.value && (
        <ModalForm<{ name: string; description: string }>
          title={`Modifica Cartella - ${renameFolderModalState.value.name}`}
          isOpen={renameFolderModalState.isOpen}
          toggle={renameFolderModalActions.toggle}
          initialValues={{
            name: renameFolderModalState.value.name,
            description: renameFolderModalState.value.description ?? "",
          }}
          onClosed={renameFolderModalActions.onClosed}
          save={confirmRenameFolder}
          schema={schema_renameFolder}
        >
          <div className="row">
            <div className="col-12">
              <VField name="name" label={"Nuovo nome"} placeholder={`${renameFolderModalState.value.name}`} />
            </div>
          </div>
          <div className="row">
            <div className="col-12">
              <Field
                component={InputField}
                name="description"
                label={"Nuova descrizione"}
                placeholder={`${renameFolderModalState.value.description ?? ""}`}
                type="textarea"
                textarea
              />
            </div>
          </div>
        </ModalForm>
      )}
      {createFolderModalState.value && (
        <ModalForm<{ name: string; description: string }>
          title={"Crea nuova cartella"}
          isOpen={createFolderModalState.isOpen}
          toggle={createFolderModalActions.toggle}
          initialValues={{ name: "", description: "" }}
          onClosed={createFolderModalActions.onClosed}
          save={createNewFolder}
          schema={schema_createFolder}
        >
          <div className="row">
            <div className="col-12">
              <VField name="name" label={"Nome cartella"} />
            </div>
          </div>
          <div className="row">
            <div className="col-12">
              <Field component={InputField} name="description" label={"Descrizione"} type="textarea" textarea />
            </div>
          </div>
        </ModalForm>
      )}
      {deleteFolderModalState.value && (
        <ModalConfirm
          isOpen={deleteFolderModalState.isOpen}
          toggle={() => {
            setDeleteError(null)
            deleteFolderModalActions.toggle()
          }}
          onClosed={() => {
            setDeleteError(null)
            deleteFolderModalActions.onClosed()
          }}
          title="Elimina Cartella"
          body={`Confermi l'eliminazione della cartella ${deleteFolderModalState.value!.name}?`}
          error={deleteError}
          okCallBack={() => confirmDeleteFolder(deleteFolderModalState.value!.id)}
          isSubmitting={isDeleteModalSubmitting}
        />
      )}
      {uploadFileModalState.value && data?.folder && (
        <UploadFileModal
          isOpen={uploadFileModalState.isOpen}
          toggle={uploadFileModalActions.toggle}
          onClosed={uploadFileModalActions.onClosed}
          idFolder={data?.folder?.id}
          onUpload={refetch}
        />
      )}
    </>
  )
}

export default FilesSystem
